import { FYIService } from "@gap/backend/app-services/fyi/FYIService"
import { FYIDto } from "@gap/backend/app-services/fyi/models/FYIDto"
import { notNull } from "@gap/utils/utils"
import { ActiveIntegrations, Analytics } from "analytics/analytics"
import ShareAnalytics from "analytics/ShareAnalytics"
import { apiClient } from "api/trpc/trpc-client"
import { IDBFYIClientsCache } from "cache/idb/IDBFYIClientsCache"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { GapDialogAction } from "reusable/gap-dialog/GapDialogActionButton"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { snackbarError, snackbarSuccess } from "reusable/mui/snackbar/snackbar"

interface AdvisorIntegrationFYIStoreProps {
   source: 'integrations' | 'share-dialog'
   orgGuardStore: OrgGuardStore
}

export default class AdvisorIntegrationFYIStore {
   connectDialog = new ModalState({
      onClose: () => this.resetFields()
   })
   clientLoader = new Loader(false)
   loader = new Loader(true)
   connectLoader = new Loader()
   status: FYIService.status = 'not connected'
   /** FYI clients loaded so far; grows page by page while `clientLoader` is loading. */
   FYIClients: FYIDto.clientMinimal[] | undefined
   /** XPM client uuid sent with the first page so the matching FYI client arrives before the rest. */
   clientXeroId: string | undefined
   fields = {
      accessId: '',
      accessSecret: ''
   }
   sp

   constructor(sp: AdvisorIntegrationFYIStoreProps) {
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         accessId: { type: 'string' },
         accessSecret: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get primaryGapDialogButton(): GapDialogAction {
      return {
         name: 'Connect',
         callback: this.connect,
         disabled: !this.fields.accessId || !this.fields.accessSecret,
         loading: this.loader.isLoading || this.connectLoader.isLoading,
         notCloseAutomatically: true
      }
   }

   resetFields = () => {
      this.fields.accessId = ''
      this.fields.accessSecret = ''
   }

   validate = () => {
      return this.formHandler.validate(FYIDto.accessTokenSchema)
   }

   connect = async () => {
      if (this.validate()) {
         await this.connectLoader.executeLoading(async () => {
            const result = await apiClient.fyi.setToken.mutate({
               accessId: this.fields.accessId,
               accessSecret: this.fields.accessSecret
            })
            if (result.status === 'successful') {
               const orgId = notNull(this.sp.orgGuardStore.orgId)
               Analytics.setActiveIntegrations(orgId, { [ActiveIntegrations.FYI]: true })
               await IDBFYIClientsCache.invalidate({ orgId })
               this.status = 'connected'
               this.connectDialog.close()
               snackbarSuccess('Sucessfully connected with FYI')
               if (this.sp.source === 'share-dialog') {
                  ShareAnalytics.connectToFYIViaShareComponent()
               }
            } else {
               snackbarError(`Unable to integrate. Reponse: ${result.message}`)
            }
         })
         await this.fetchClients()
      }
   }

   disconnect = async () => {
      await this.loader.executeLoading(async () => {
         await apiClient.fyi.disconnect.mutate()
         const orgId = notNull(this.sp.orgGuardStore.orgId)
         Analytics.setActiveIntegrations(orgId, { [ActiveIntegrations.FYI]: false })
         await IDBFYIClientsCache.invalidate({ orgId })
         this.status = 'not connected'
         snackbarSuccess('Sucessfully disconnected with FYI')
      })
   }

   fetch = async (params?: { xeroId?: string }) => {
      this.clientXeroId = params?.xeroId
      await this.loader.executeLoading(async () => {
         this.status = await apiClient.fyi.getConnectionStatus.query()
      })
      await this.fetchClients()
   }

   fetchClients = async () => {
      if (this.status !== 'connected' || this.sp.source !== 'share-dialog') {
         return
      }
      await this.clientLoader.executeLoading(async () => {
         this.FYIClients = []
         const clients = await IDBFYIClientsCache.get({
            orgId: notNull(this.sp.orgGuardStore.orgId),
            xeroId: this.clientXeroId,
            onClientsLoaded: this.setFYIClients,
         })
         if (clients) {
            this.setFYIClients(clients)
         }
      })
   }

   private setFYIClients = (clients: FYIDto.clientMinimal[]) => {
      this.FYIClients = clients
   }

}

export const { Provider: AdvisorIntegrationFYIStoreProvider, useProvidedStore: useAdvisorIntegrationFYIStore } = createStoreContext(AdvisorIntegrationFYIStore)
